import { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Button, Loading } from 'framework/ui/mui-builder';
import { Modal } from 'framework/ui/ui-builder';
import { getErrorMessage } from '../utils/format';

type TextDialogProps = {
  title: string;
  fileName: string;
  /** Loads the text; called every time the dialog opens. */
  load: () => Promise<string>;
  onClose: () => void;
};

const downloadText = (fileName: string, text: string) => {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

/** Shows a text document of the core service (YAML, JSON) that cannot be opened by a plain link because it needs the token. */
const TextDialog = ({ title, fileName, load, onClose }: TextDialogProps) => {
  const [text, setText] = useState<Nullable<string>>(null);
  const [error, setError] = useState<Nullable<string>>(null);

  useEffect(() => {
    let isActive = true;

    load()
      .then(result => isActive && setText(result))
      .catch(reason => isActive && setError(getErrorMessage(reason)));

    return () => {
      isActive = false;
    };
    // the loader identity changes on every render of the parent; the dialog is re-created per document
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = useCallback(() => {
    if (text) {
      navigator.clipboard?.writeText(text);
    }
  }, [text]);

  return (
    <Modal
      isOpen
      title={title}
      modalSize="md"
      hasCloseIcon
      hasCancelButton={false}
      onToggle={onClose}
      buttons={
        <>
          <Button variant="outlined" disabled={!text} onClick={copy}>
            Copy
          </Button>
          <Button variant="outlined" disabled={!text} onClick={() => text && downloadText(fileName, text)}>
            Download
          </Button>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {error ? <Typography color="error">{error}</Typography> : null}
      {text === null && !error ? <Loading isInitialPosition /> : null}
      {text !== null ? (
        <Box
          component="pre"
          sx={{
            margin: 0,
            maxHeight: '60vh',
            overflow: 'auto',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {text}
        </Box>
      ) : null}
    </Modal>
  );
};

export default TextDialog;
